import { Component, ViewChild } from '@angular/core';
import { AssignMiscOrder, AssignOrder, CustomerAddress, Customers, DriverCard, Orders, Drivers } from '../interfaces';
import { DataServiceService } from '../services/data-service.service';
import { filter, forkJoin, Subscription, switchMap } from 'rxjs';
import { DateTime } from 'luxon';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DriverPreferencesService } from '../services/driver-preferences.service';
import { AuthService } from '../services/auth.service';
import { SendDispatchComponent } from '../dispatch/send-dispatch/send-dispatch.component';
import {
  formatDispatchOrdersForCard,
  getTruckContactForDriver,
  TruckContact,
} from '../dispatch/send-dispatch/send-dispatch.helpers';
import { Router } from '@angular/router';
import { AssignEquipmentComponent } from '../equipment/assign-equipment/assign-equipment.component';
import { EquipmentEditDialogComponent } from '../dispatch-dash-dialogs/equipment-edit-dialog/equipment-edit-dialog.component';
import { TimeEditDialogComponent } from '../dispatch-dash-dialogs/time-edit-dialog/time-edit-dialog.component';
import { SaltTypeDialogComponent } from '../dispatch-dash-dialogs/salt-type-dialog/salt-type-dialog.component';
import { AssignToNewOrderDialogComponent } from '../dispatch-dash-dialogs/assign-to-new-order-dialog/assign-to-new-order-dialog.component';
import { MiscTaskDialogComponent } from '../dispatch-dash-dialogs/misc-task-dialog/misc-task-dialog.component';
import { WashoutDialogComponent } from '../dispatch-dash-dialogs/washout-dialog/washout-dialog.component';
import { StatusUpdateDialogComponent } from '../dispatch-dash-dialogs/status-update-dialog/status-update-dialog.component';
import { OverlayPanel } from 'primeng/overlaypanel';

@Component({
  selector: 'app-dispatch-dashboard',
  templateUrl: './dispatch-dashboard.component.html',
  styleUrl: './dispatch-dashboard.component.scss'
})
export class DispatchDashboardComponent {


  @ViewChild('dispatchDialog') dispatchDialog!: SendDispatchComponent;
  @ViewChild('equipmentAssigner') equipmentAssigner!: AssignEquipmentComponent;
  @ViewChild('equipmentEditDialog') equipmentEditDialog!: EquipmentEditDialogComponent;
  @ViewChild('timeEditDialog') timeEditDialog!: TimeEditDialogComponent;
  @ViewChild('saltTypeDialog') saltTypeDialog!: SaltTypeDialogComponent;
  @ViewChild('assignToNewOrderDialog') assignToNewOrderDialog!: AssignToNewOrderDialogComponent;
  @ViewChild('miscTaskDialog') miscTaskDialog!: MiscTaskDialogComponent;
  @ViewChild('washoutDialog') washoutDialog!: WashoutDialogComponent;
  @ViewChild('statusUpdateDialog') statusUpdateDialog!: StatusUpdateDialogComponent;
  @ViewChild('trailerOverlay') trailerOverlay!: OverlayPanel;

  constructor(
    private dataServiceService: DataServiceService,
    private messageService: MessageService, 
    private confirmationService: ConfirmationService,
    private driverPreferencesService: DriverPreferencesService,
    private authService: AuthService,
    private router: Router
  ) { }

  private subscription: Subscription | undefined;

  driverCards: DriverCard[] = [];
  filteredCards: DriverCard[] = [];
  temporarilyFilteredCards: DriverCard[] = [];
  isTemporaryFilterActive: boolean = false;
  allOrders: Orders[] = [];
  customers: Customers[] = [];
  selectedDriverIds: number[] = [];

  sidebarVisible: boolean = false;
  dispatchSidebarOpen: boolean = false;

  editingOrderId: number | null = null;
  trailerEditValue: string = '';
  trailerSuggestions: string[] = [];

  draggedDriver: any;
  draggedOrder: any = null;
  draggedOver: number | null = null;
  originalOrderPosition: { driver: DriverCard, orderIndex: number } | null = null;

  selectedDriverDispatch: number = -1;
  selectedOrderId: number = -1;

  trailers: any[] = [];
  trailerMap: { [key: string]: string } = {};

  hasAccess = false;

  previousDispatchOrderIds: Set<number> = new Set();

  selectedOrder: any = null;

  selectedDate: Date | null = null;

  addresses: CustomerAddress[] = [];
  addressMap: { [key: string]: CustomerAddress } = {};

  trucks: any[] = [];

  drivers: Drivers[] = [];

  ngOnInit() {
    // Subscribe to driver preferences changes first
    this.driverPreferencesService.selectedDriverIds$.subscribe(ids => {
      this.selectedDriverIds = ids;
      // Only apply preferences if we already have driver cards
      if (this.driverCards.length > 0) {
        this.applyDriverPreferences();
      }
    });

    // Subscribe to date changes and store the date
    this.dataServiceService.selectedDate$.pipe(
      filter(date => !!date),
      switchMap(date => {
        // Convert the date string to a DateTime object in Central time
        const centralDate = DateTime.fromFormat(date, 'yyyy-MM-dd', { zone: 'America/Chicago' });
        this.selectedDate = centralDate.toJSDate();
        return this.dataServiceService.getDailyDispatch(date);
      })
    ).subscribe({
      next: (response) => {
        this.driverCards = response;
        this.applyDriverPreferences();
      },
      error: (error) => {
        console.error('Error fetching dispatch:', error);
      }
    });

    // This single subscription will handle both HTTP and WebSocket updates
    this.dataServiceService.getDriverCardsObservable().subscribe(cards => {
      if (cards && cards.length > 0) {
        this.driverCards = cards;
        this.applyDriverPreferences();
      }
    });
    this.authService.userProfile$.subscribe((profile) => {
      if (profile?.roles?.includes('Dispatcher') || profile?.roles?.includes('Admin')) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
      }
    });

    this.dataServiceService.getAllTrucks().subscribe((data: any) => {
      this.trucks = data;
      this.trucks.sort((a: { truck_number: string; }, b: { truck_number: string; }) => a.truck_number.localeCompare(b.truck_number, undefined, {numeric: true}));
      // Update the assign-equipment component with latest truck data
      if (this.equipmentAssigner) {
        this.equipmentAssigner.trucks = this.trucks;
      }
    });
    this.dataServiceService.getAllDrivers().subscribe((data: any) => {
      this.drivers = data;
      // Update the assign-equipment component with latest driver data
      if (this.equipmentAssigner) {
        this.equipmentAssigner.drivers = this.drivers;
      }
    });


    // Subscribe to WebSocket events for dispatch orders (split orders and misc tasks)
    this.dataServiceService.getDispatchOrdersObservable().subscribe((dispatchOrders: any[]) => {
      
      // Check for deleted dispatch orders
      const currentDispatchOrderIds = new Set(dispatchOrders.map(order => order.id));
      
      // Find deleted dispatch orders by comparing with previous state
      if (this.previousDispatchOrderIds) {
        const deletedOrderIds = [...this.previousDispatchOrderIds].filter(id => !currentDispatchOrderIds.has(id));
        
        // Handle deleted dispatch orders
        if (deletedOrderIds.length > 0) {
          // Find the driver and dispatch information for the deleted order
          const deletedOrderId = deletedOrderIds[0];
          
          for (const card of this.driverCards) {
            const dispatchOrder = card.dispatchOrders.find(order => order.id === deletedOrderId);
            if (dispatchOrder) {
              // If the order_id is null, it's a misc task
              if (dispatchOrder.order_id === null) {
                this.handleMiscTaskDeleted(card.driver.driver_id, deletedOrderId);
              }
              break;
            }
          }
        }
      }
      
      // Store current state for next comparison
      this.previousDispatchOrderIds = new Set(currentDispatchOrderIds);
      
      // Check if any new dispatch orders have been added
      if (dispatchOrders && dispatchOrders.length > 0) {
        const latestDispatchOrder = dispatchOrders[dispatchOrders.length - 1];
        // Check if this is a new dispatch order assignment
        if (latestDispatchOrder && latestDispatchOrder.dispatch_id) {
        
        }
      }
    });

    // Add a direct subscription to the socket service for misc_task_deleted events
    this.subscription = this.dataServiceService.onMiscTaskDeleted().subscribe((data: any) => {
      if (data && data.driver_id && data.task_id) {
        this.handleMiscTaskDeleted(data.driver_id, data.task_id);
      }
    });

    this.dataServiceService.orders$.subscribe(orders => {
      {
        this.allOrders = orders
      }
    });
    this.dataServiceService.getAllTrailers().subscribe(data => {
      this.trailers = data;
      if (this.equipmentAssigner) {
        this.equipmentAssigner.trailers = this.trailers;
      }
      this.trailerMap = this.trailers.reduce((map: { [key: string]: string }, trailer: { trailer_id: number; trailer_number: string }) => {
        map[trailer.trailer_id.toString()] = trailer.trailer_number;
        return map;
      }, {});
      this.trailers.sort((a: { trailer_number: string; }, b: { trailer_number: string; }) => a.trailer_number.localeCompare(b.trailer_number, undefined, {numeric: true}));
    });

    this.dataServiceService.getAllCustomers().subscribe(data => {
      this.customers = data;
      
      // Extract all addresses from all customers into flat arrays and create lookup map
      this.addresses = [];
      this.addressMap = {};
      
      this.customers.forEach(customer => {
        if (customer.addresses && customer.addresses.length > 0) {
          customer.addresses.forEach(address => {
            this.addresses.push(address);
            this.addressMap[address.address_id.toString()] = address;
          });
        }
      });
    });
  }


  // Apply driver preferences to filter the displayed drivers
  applyDriverPreferences(): void {
    
    // If no drivers are selected in preferences, show all drivers
    if (!this.selectedDriverIds || this.selectedDriverIds.length === 0) {
      this.filteredCards = this.driverCards;
      return;
    }

    // Filter driver cards based on selected driver IDs
    this.filteredCards = this.driverCards.filter(card => 
      this.selectedDriverIds.includes(card.driver.driver_id)
    );
  }

  getTrailerNumber(trailerId: number | null): string {
    if (!trailerId) return 'None Assigned';
    const trailer = this.trailers.find(t => t.trailer_id === trailerId);
    return trailer?.trailer_number || 'None Assigned';
  }

  openTrailerEdit(event: Event, order: any) {
    if (!this.hasAccess) return;
    this.editingOrderId = order.order_id;
    this.trailerEditValue = order.trailer_id
      ? this.getTrailerNumber(order.trailer_id)
      : (order.external_trailer_number ?? '');
    this.trailerOverlay.toggle(event);
  }

  filterTrailerSuggestions(event: { query: string }) {
    const q = event.query.toLowerCase();
    this.trailerSuggestions = this.trailers
      .map((t: any) => t.trailer_number)
      .filter((n: string) => n.toLowerCase().includes(q));
  }

  saveTrailerEdit() {
    if (!this.editingOrderId) return;
    const matched = this.trailers.find(
      (t: any) => t.trailer_number.toLowerCase() === this.trailerEditValue.trim().toLowerCase()
    );
    const payload = matched
      ? { trailer_id: matched.trailer_id, external_trailer_number: '' }
      : { trailer_id: null, external_trailer_number: this.trailerEditValue.trim() };
    this.dataServiceService.updateOrder(this.editingOrderId, payload).subscribe();
    this.trailerOverlay.hide();
    this.editingOrderId = null;
  }

  ngOnDestroy() {
    // Unsubscribe from the misc task deleted subscription
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  /** Truck inbox + label for one driver (used by send dialog and per-row speed dial). */
  truckContactForCard(card: DriverCard): TruckContact {
    return getTruckContactForDriver(card.driver.driver_id, this.drivers, this.trucks);
  }

  openSendDispatchDialog(card: DriverCard) {
    const text = formatDispatchOrdersForCard(
      card,
      this.selectedDate,
      this.allOrders,
      this.customers,
      this.trailers
    );
    this.dispatchDialog.showEmailDialog(text, this.truckContactForCard(card));
  }

  getCustomerName(customerId: number): string {
    const customer = this.customers.find(c => c.customer_id === customerId);
    if (customer) {
    if (customer.name.length > 15) {
      return customer.name.substring(0, 15) + '...';
    }
    return customer ? customer.name || 'Unknown' : 'Unknown';
  } else {
  return 'Unknown';
  }
  }

  filterDriversBySelected(drivers: DriverCard[], isStatusFilter: boolean): DriverCard[] {
    if (drivers && drivers.length > 0) {
      return this.driverCards.filter(card =>
        drivers.some(selectedDriver =>
          selectedDriver.driver.driver_id === card.driver.driver_id
        )
      );
    }
    // If called from the status function, return an empty array.
    if (isStatusFilter) {
      return [];
    }

    // If called from the driver selected function, return all driver cards.
    return this.driverCards;
  }  

  onDriverSelected(drivers: DriverCard[]) {
    // For temporary search filtering
    if (drivers && drivers.length > 0) {
      // Apply temporary search filter
      this.temporarilyFilteredCards = this.filteredCards.filter(card =>
        drivers.some(selectedDriver =>
          selectedDriver.driver.driver_id === card.driver.driver_id
        )
      );
      this.isTemporaryFilterActive = true;
    } else {
      // Clear temporary search filter, return to preference-filtered view
      this.clearTemporaryFilter();
    }
  }

  onDriverStatusSelected(drivers: DriverCard[]) {
    // For temporary status filtering
    if (drivers && drivers.length >= 0) {
      // Apply temporary status filter
      this.temporarilyFilteredCards = this.filteredCards.filter(card =>
        drivers.some(selectedDriver =>
          selectedDriver.dailyDispatch.daily_driver_status === card.dailyDispatch.daily_driver_status
        )
      );
      this.isTemporaryFilterActive = true;
    } else {
      // Clear temporary status filter, return to preference-filtered view
      this.clearTemporaryFilter();
    }
  }

  // Method to clear temporary filters
  clearTemporaryFilter() {
    this.isTemporaryFilterActive = false;
    this.temporarilyFilteredCards = [];
  }

  // Method to update preference-based filtering
  updatePreferenceFiltering(preferredDrivers: DriverCard[]) {
    this.filteredCards = this.filterDriversBySelected(preferredDrivers, false);
  }

  // Method to determine which driver list to display
  getDisplayedDrivers(): DriverCard[] {
    const drivers = this.isTemporaryFilterActive ? this.temporarilyFilteredCards : this.filteredCards;
    return drivers.sort((a, b) => {
      const lastNameCmp = a.driver.last_name.localeCompare(b.driver.last_name);
      if (lastNameCmp !== 0) return lastNameCmp;
      return a.driver.first_name.localeCompare(b.driver.first_name);
    });
  }

  // Opening dispatch side bar
  onSidebarStateChanged(isOpen: boolean) {
    this.dispatchSidebarOpen = isOpen
    this.sidebarVisible = isOpen;
  }

  updateDailyDispatch(selectedDriverDispatchId: number) {
    const driver = this.driverCards.find(
      card => card.driver.driver_id === selectedDriverDispatchId
    );

    if (driver?.dailyDispatch) {
      const dispatchId = driver.dailyDispatch.id;
      const updates = {
        notes: driver.dailyDispatch.notes,
        daily_driver_status: driver.dailyDispatch.daily_driver_status
      };

      this.dataServiceService
        .updateDriverDailyDispatch(dispatchId, selectedDriverDispatchId, updates)
        .subscribe({
          next: response => {
            console.log('Dispatch updated successfully:', response);
          },
          error: err => console.error('Error updating dispatch:', err)
        });
    } else {
      console.error('Driver or dailyDispatch not found for ID:', selectedDriverDispatchId);
    }
  }

  updateDailyDispatchWithValue(selectedDriverDispatchId: number, event: Event) {
    const target = event.target as HTMLTextAreaElement;
    const notesValue = target.value;
    
    const driver = this.driverCards.find(
      card => card.driver.driver_id === selectedDriverDispatchId
    );

    if (driver?.dailyDispatch) {
      // Update the model with the actual textarea value to keep UI in sync
      driver.dailyDispatch.notes = notesValue;
      
      const dispatchId = driver.dailyDispatch.id;
      const updates = {
        notes: notesValue,
        daily_driver_status: driver.dailyDispatch.daily_driver_status
      };

      console.log('Saving notes for driver:', selectedDriverDispatchId, 'Notes value:', notesValue);

      this.dataServiceService
        .updateDriverDailyDispatch(dispatchId, selectedDriverDispatchId, updates)
        .subscribe({
          next: response => {
            console.log('Dispatch updated successfully:', response);
          },
          error: err => console.error('Error updating dispatch:', err)
        });
    } else {
      console.error('Driver or dailyDispatch not found for ID:', selectedDriverDispatchId);
    }
  }

  updateAvailability(driverId: number, event: any) {
    const isChecked = event.checked;
    const newStatus = isChecked ? 'Unavailable' : 'Available';

    const driver = this.driverCards.find(card => card.driver.driver_id === driverId);
    if (driver?.dailyDispatch) {
      driver.dailyDispatch.daily_driver_status = newStatus;
      this.updateDailyDispatch(driverId);
    } else {
      console.error('Driver or dailyDispatch not found.');
    }
  }

  onOrderDragged(order: Orders) {
    this.draggedOrder = order
  }

  onUnassignOrder() {
    if (!this.draggedOrder || !this.draggedDriver) return;
    this.dataServiceService.removeOrderFromDriverDispatch(
      this.draggedDriver.driver.driver_id,
      this.draggedDriver.dailyDispatch.id,
      this.draggedOrder.order_id,
      'Unassigned'
    ).subscribe();
    this.draggedOrder = null;
    this.draggedDriver = null;
    this.originalOrderPosition = null;
  }

  onLoadDragStart(order: any, driver: DriverCard) {
    const originalOrder = order;
    order = this.allOrders.find(o => o.order_id === order.order_id)

    this.draggedOrder = {
      order_id: order.order_id,
      pickup_driver_id: order.pickup_driver_id,
      delivery_driver_id: order.delivery_driver_id,
      load_status: order.load_status,
    };
    this.draggedDriver = driver;
    
    // Track original position for potential restoration
    const orderIndex = driver.dispatchOrders.findIndex(o => 
      (o.order_id && o.order_id === originalOrder.order_id) || 
      (o.id && o.id === originalOrder.id)
    );
    this.originalOrderPosition = { driver, orderIndex };
  }

  onLoadDragEnd() {
    // Check if order was dropped in valid area (draggedOrder will be null if successfully dropped)
    if (this.draggedOrder && this.originalOrderPosition) {
      // Order was not dropped in valid area, restore to original position
      this.restoreOrderToOriginalPosition();
    }
    
    this.draggedOrder = null;
    this.draggedDriver = null;
    this.originalOrderPosition = null;
  }

  restoreOrderToOriginalPosition() {
    if (!this.originalOrderPosition || !this.draggedOrder) return;

    const { driver, orderIndex } = this.originalOrderPosition;
    
    // Find the order that was moved
    const orderToRestore = driver.dispatchOrders.find(o => 
      (o.order_id && o.order_id === this.draggedOrder.order_id) || 
      (o.id && o.id === this.draggedOrder.id)
    );

    if (orderToRestore) {
      // Remove from current position
      const currentIndex = driver.dispatchOrders.indexOf(orderToRestore);
      if (currentIndex > -1) {
        driver.dispatchOrders.splice(currentIndex, 1);
      }

      // Restore to original position
      if (orderIndex >= 0 && orderIndex <= driver.dispatchOrders.length) {
        driver.dispatchOrders.splice(orderIndex, 0, orderToRestore);
      }
    }
  }

  onDrop(targetDriver: DriverCard, draggedDriver?: DriverCard) {
    if (!targetDriver || !this.draggedOrder) return;
    if (targetDriver.dailyDispatch?.daily_driver_status !== 'Unavailable') {
      // For new order assignment (no draggedDriver)
      if (!draggedDriver) {
        const order = this.allOrders.find(o => o.order_id === this.draggedOrder.order_id);

        if (order) {
          // Always set the target driver selection
          this.selectedDriverDispatch = targetDriver.driver.driver_id;

          // Determine what is missing
          const missingTrailer = !order.trailer_id && !order.external_trailer_number;
          const missingDates = !order.pickup_date || !order.delivery_date;

          if (missingTrailer || missingDates) {
            this.assignToNewOrderDialog.open(order, targetDriver.driver.driver_id, this.trailers);
            return;
          }
          else {
            // Nothing missing – proceed with assignment
            this.assignOrderToDriver(targetDriver);
          }
          return;
        }

        this.assignOrderToDriver(targetDriver);
      }
      // For reassigning orders between drivers
      else if (draggedDriver) {
        // Check if this is a reorder within the same driver (no actual reassignment needed)
        if (targetDriver.driver.driver_id === draggedDriver.driver.driver_id) {
          // This is just a reorder within the same driver, no backend changes needed
          this.draggedOrder = null;
          return;
        }
        
        const fullOrder = this.allOrders.find(o => o.order_id === this.draggedOrder.order_id);

        // Check if this is a split load
        const isSplitLoad = fullOrder &&
          fullOrder.pickup_driver_id !== fullOrder.delivery_driver_id &&
          (fullOrder.pickup_driver_id === draggedDriver.driver.driver_id ||
            fullOrder.delivery_driver_id === draggedDriver.driver.driver_id);

        if (isSplitLoad) {
          const orderData = {
            order_id: this.draggedOrder.order_id,
            priority: String(targetDriver.dispatchOrders?.length + 1 || 1),
            scheduled_time: new Date().toISOString(),
            notes: this.draggedOrder.notes || ''
          };

          // Determine if the dragged driver is the pickup driver
          const isPickupDriver = fullOrder.pickup_driver_id === draggedDriver.driver.driver_id;

          // Ensure the correct driver IDs stay with the correct roles
          const updatedDrivers = {
            pickup_driver_id: isPickupDriver ? targetDriver.driver.driver_id : fullOrder.pickup_driver_id,
            delivery_driver_id: isPickupDriver ? fullOrder.delivery_driver_id : targetDriver.driver.driver_id,
            load_status: fullOrder.load_status || 'Scheduled'
          };

          forkJoin({
            remove: this.dataServiceService.removeOrderFromDriverDispatch(
              draggedDriver.driver.driver_id,
              draggedDriver.dailyDispatch.id,
              this.draggedOrder.order_id
            ),
            updateDrivers: this.dataServiceService.updateOrder(
              this.draggedOrder.order_id,
              updatedDrivers
            ),
            assign: this.dataServiceService.assignSplitOrderToDispatch(
              targetDriver.driver.driver_id,
              targetDriver.dailyDispatch.id,
              orderData
            )
          }).subscribe({
            next: () => {
              this.draggedOrder = null;
              this.dataServiceService.refreshDriverCards();
            },
            error: (error) => console.error('Failed during split load reassignment:', error)
          });

        } else {
          // Regular non-split load reassignment
          const orderData: AssignOrder = {
            order_id: this.draggedOrder.order_id,
            priority: String(targetDriver.dispatchOrders?.length + 1 || 1),
            scheduled_time: new Date().toISOString(),
            notes: this.draggedOrder.notes,
            pickup_driver_id: targetDriver.driver.driver_id,
            delivery_driver_id: targetDriver.driver.driver_id,
            load_status: fullOrder?.load_status || 'Scheduled'
          };

          forkJoin({
            remove: this.dataServiceService.removeOrderFromDriverDispatch(
              draggedDriver.driver.driver_id,
              draggedDriver.dailyDispatch.id,
              this.draggedOrder.order_id
            ),
            assign: this.dataServiceService.assignOrderToDriverDispatch(
              targetDriver.driver.driver_id,
              targetDriver.dailyDispatch.id,
              orderData
            )
          }).subscribe({
            next: (results) => {
              this.draggedOrder = null;
            },
            error: (error) => {
              console.error('Failed during reassignment:', error);
            }
          });
        }
      }
    }
  }

  assignOrderToDriver(targetDriver: DriverCard) {
    const orderData: AssignOrder = {
      order_id: this.draggedOrder.order_id,
      priority: String(targetDriver.dispatchOrders?.length + 1 || 1),
      scheduled_time: new Date().toISOString(),
      notes: this.draggedOrder.notes || ''
    };

    this.dataServiceService.assignOrderToDriverDispatch(
      targetDriver.driver.driver_id,
      targetDriver.dailyDispatch.id,
      orderData
    ).subscribe({
      next: () => this.draggedOrder = null,
      error: (error) => console.error('Failed to assign order:', error)
    });
  }

  getOrderDetails(orderId: number): Orders | undefined {
    return this.allOrders.find(order => order.order_id === orderId);
  }

  // Sort dispatch orders by priority to ensure consistent display order
  getSortedDispatchOrders(dispatchOrders: any[]): any[] {
    if (!dispatchOrders || dispatchOrders.length === 0) {
      return [];
    }
    
    return [...dispatchOrders].sort((a, b) => {
      const priorityA = parseInt(a.priority) || 0;
      const priorityB = parseInt(b.priority) || 0;
      return priorityA - priorityB;
    });
  }

  onNotesSaved(event: { driverId: number, orderId: number, notes: string }) {
    let driver = this.driverCards.find(d => d.driver.driver_id == event.driverId);
    if (driver) {
      let load = driver.dispatchOrders.find(l => l.order_id == event.orderId);
      if (load) {
        load.notes = event.notes;
      }
    }
  }

  openWashoutDialog(orderId: number) {
    // Update the selected order ID for the washout dialog
    this.selectedOrderId = orderId;
    // Open the washout dialog
    this.washoutDialog.openDialog();
  }

  toCentralTime(date: string | Date): string {
    const dt = DateTime.fromISO(date.toString(), { zone: 'utc' });
    
    // Check if the time is midnight in UTC (meaning no specific time was selected)
    if (dt.hour === 0 && dt.minute === 0 && dt.second === 0) {
      const centralTime = dt.setZone('America/Chicago');
      return centralTime.toFormat('MM/dd/yyyy');
    }
    
    // Time was specified, convert to Central and show time
    const centralTime = dt.setZone('America/Chicago');
    return centralTime.toFormat('h:mm a');
  }

  toCentralDateTime(date: string | Date): string {
    const dt = DateTime.fromISO(date.toString(), { zone: 'utc' }).setZone('America/Chicago');
    
    // Check if original UTC time is midnight
    if (dt.hour === 0 && dt.minute === 0) {
        return dt.toFormat('MM/dd/yyyy');
    }
    
    // Compare with selected dispatch date
    if (this.selectedDate) {
      const dispatchDate = DateTime.fromJSDate(this.selectedDate);
      const appointmentDate = dt.startOf('day');
      const currentDispatchDate = dispatchDate.startOf('day');
      
      // If same date, show only time
      if (appointmentDate.equals(currentDispatchDate)) {
        return dt.toFormat('h:mm a');
      }
    }
    
    // Different date or no dispatch date selected, show date + time
    return dt.toFormat('MM/dd/yyyy h:mm a');
  }
  

  openMiscDialog(driverId: number) {
    this.miscTaskDialog.openMiscDialog(driverId);
  }

  onMiscNotesFocus(order: any) {
    order._originalNotes = order.notes;
  }

  saveMiscTaskEdit(order: any, card: any) {
    if (order.notes === order._originalNotes) return;
    const driverId = card.driver.driver_id;
    const dispatchId = this.getDailyDispatchId(driverId);
    if (dispatchId && order.id) {
      this.dataServiceService.updateMiscTask(driverId, dispatchId, order.id, {
        notes: order.notes,
        scheduled_time: order.scheduled_time,
      }).subscribe();
    }
  }

  getDailyDispatchId(driverId: number): number | undefined {
    const driver = this.driverCards.find(d => d.driver.driver_id === driverId);
    return driver?.dailyDispatch?.id;
  }

  confirmRemove(event: any, order: any, driver: any) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to delete this task?',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-success p-button-outlined p-button-sm',
      rejectButtonStyleClass: 'p-button-text p-button-sm',
      accept: () => {
        
        const driverId = driver.driver.driver_id;
        const dispatchId = this.getDailyDispatchId(driverId);
        const taskId = order.id
        console.log(dispatchId, taskId)
        if (dispatchId !== undefined && taskId !== undefined) {
          this.dataServiceService.removeMiscTaskFromDriverDispatch(driverId, dispatchId, taskId).subscribe();
        } else {
          console.error('Missing required data for removing misc task.');
        }
        this.messageService.add({ severity: 'info', summary: 'Confirmed', detail: 'Task deleted', life: 3000 });
      },
      reject: () => {
        this.messageService.add({ severity: 'error', summary: 'Rejected', detail: 'Task was not deleted', life: 3000 });
      }
    });
  }

  getSplitLoadTooltip(load: any, driver: any): string {
    const pickupDriver = this.driverCards.find(d => d.driver.driver_id === load.pickup_driver_id);
    const dropOffDriver = this.driverCards.find(d => d.driver.driver_id === load.delivery_driver_id);

    // If current card is for pickup driver, show dropoff driver and vice versa
    if (driver.driver_id === load.pickup_driver_id) {
      return `Split Load:
    Drop-off: ${dropOffDriver?.driver.first_name || 'Unknown'} ${dropOffDriver?.driver.last_name || ''}`;
    } else {
      return `Split Load:
    Pickup: ${pickupDriver?.driver.first_name || 'Unknown'} ${pickupDriver?.driver.last_name || ''}`;
    }
  }


  // Add this method to handle split orders
  handleSplitOrderAssigned(driverId: number, orderId: number) {
    // Find the order details
    const orderDetails = this.getOrderDetails(orderId);
    if (!orderDetails) {
      console.error('Order not found for split assignment:', orderId);
      return;
    }

    // Find the driver card
    const driverCardIndex = this.driverCards.findIndex(card => card.driver.driver_id === driverId);
    if (driverCardIndex === -1) {
      console.error('Driver not found for split assignment:', driverId);
      return;
    }

    // Show a success message
    this.messageService.add({
      severity: 'success',
      summary: 'Order Split',
      detail: `Order #${orderDetails.pickup_number || orderId} has been split and assigned to ${this.driverCards[driverCardIndex].driver.first_name} ${this.driverCards[driverCardIndex].driver.last_name}`,
      life: 3000
    });
  }


  // Add this method to handle misc tasks deleted
  handleMiscTaskDeleted(driverId: number, taskId: number) {
    // Find the driver card
    const driverCardIndex = this.driverCards.findIndex(card => card.driver.driver_id === driverId);
    if (driverCardIndex === -1) {
      console.error('Driver not found for misc task deletion:', driverId);
      return;
    }
  }

  // Get receiver address for tooltip  
  getReceiverAddressTooltip(order: any): string {
    let receiver = this.customers.find(c => c.customer_id === order.receiver_id);
    let toolTip = receiver?.name || 'Unknown';

    if (order.receiver_address_id) {
      const address = this.addressMap[order.receiver_address_id.toString()];
      if (address) {
        toolTip += `\n${address.city}, ${address.state} ${address.zipcode}`;
      }
    }
    return toolTip;
  }


  getSupplierAddressTooltip(order: any): string {
    let customer = this.customers.find(c => c.customer_id === order.supplier_id);
    let toolTip = customer?.name || 'Unknown';

    if (order.supplier_address_id) {
      const address = this.addressMap[order.supplier_address_id.toString()];
      if (address) {
        toolTip += `\n${address.city}, ${address.state} ${address.zipcode}`;
      }
    }
    return toolTip;
  }

  // Helper method to check if an order is cancelled
  isOrderCancelled(orderId: number | null): boolean {
    if (!orderId) return false;
    const order = this.allOrders.find(o => o.order_id === orderId);
    return order?.load_status === 'Cancelled';
  }


  navigateToCustomer(order: any, type: string) {
    if (type === 'supplier') {
      const customerId = order.supplier_id;
      const addressId = order.supplier_address_id;

      this.router.navigate(['/customers'], { 
        queryParams: { 
          customerId: customerId,
          addressId: addressId,
          scrollTo: true
        }
      });
    } else {
      const customerId = order.receiver_id;
      const addressId = order.receiver_address_id;

      this.router.navigate(['/customers'], { 
        queryParams: { 
          customerId: customerId,
          addressId: addressId,
          scrollTo: true
        }
      });
    }
  }
}
